/**
 * @function useScheduledContent
 * @author hzihhuang
 * @description
 * 提供基于时间调度的内容选择功能，支持通过 URL 参数 `_timer` 覆盖当前时间，
 * 以实现对未来或过去内容的预览。
 *
 * 导出：
 * - getScheduledContent(timeMap, systemTime): 纯函数，根据时间映射表和当前时间，返回最合适的内容。
 * - useScheduledContent(timeMap): React Hook，内部使用 getScheduledContent，结合全局系统时间和 URL 参数，返回当前应展示的内容。
 *
 * 时间映射表的键为日期字符串（支持包含时间），值为 React 节点或字符串内容。
 * 当前时间默认使用系统时间，可被 URL 中 `_timer` 参数覆盖，支持灵活调试和预览。
 */

import { useMemo } from 'react';
import { useGlobal } from '../useGlobal';

type TimeMap = Record<string, React.ReactNode | string>;

/**
 * 从 URL 查询参数中读取 _timer，返回对应的时间戳（毫秒）。
 * 返回 null 表示无有效覆盖时间。
 */
function getOverrideDate(): number | null {
    if (typeof window === 'undefined') return null;

    const urlParams = new URLSearchParams(window.location.search);
    const timerParam = urlParams.get('_timer');
    if (!timerParam) return null;

    const date = new Date(timerParam);
    if (isNaN(date.getTime())) return null;

    return date.getTime();
}

/**
 * 纯函数，根据时间映射表和当前时间，返回“最接近但不晚于当前时间”的内容。
 * @param timeMap - 日期字符串到内容的映射对象，支持 React 节点或字符串。
 * @param systemTime - 当前系统时间戳（毫秒）。
 * @returns 当前应显示的内容，找不到则返回 null。
 */
export function getScheduledContent<T>(timeMap: Record<string, T>, systemTime: number): T | undefined {
    const now = getOverrideDate() ?? (systemTime || Date.now());
    let latestDate = -Infinity;
    let latestContent: T | undefined = undefined;
    for (const [dateStr, content] of Object.entries(timeMap)) {
        const date = new Date(dateStr).getTime();
        if (date <= now && date > latestDate) {
            latestDate = date;
            latestContent = content;
        }
    }
    return latestContent;
}

/**
 * React Hook，返回当前应展示的调度内容。
 * 内部会结合全局系统时间和 URL 参数 `_timer` 来确定当前时间。
 * @param timeMap - 日期字符串到内容的映射对象。
 * @returns 当前应展示的 React 节点或字符串，找不到则返回 null。
 */
export function useScheduledContent(timeMap: TimeMap): React.ReactNode | string | null {
    const { getSystemTime } = useGlobal();
    const systemTime = getSystemTime();

    const now = useMemo(() => {
        return getOverrideDate() ?? systemTime;
    }, [systemTime]);

    return useMemo(() => {
        return getScheduledContent(timeMap, now);
    }, [timeMap, now]);
}
